<template>
	<div id="app">
		<transition name="fade" mode="out-in">
			<router-view />
		</transition>
	</div>
</template>

<script>
	/**
	 * App.vue - 主应用容器
	 * 功能：
	 *  1. rem 响应式字体
	 *  2. 窗口宽度断点存储
	 *  3. 监听 .tenant_search 高度，动态调整 .Mycell 高度
	 */
	export default {
		name: 'App',

		data() {
			return {
				// 用于清理的引用
				_fontSizeListener: null,
				_resizeListener: null,
				_ro: null, // ResizeObserver
				_interval: null, // 轮询定时器
				_mo: null // MutationObserver（监听 DOM 变化）
			};
		},

		mounted() {
			this.initResponsiveFont();
			this.initBreakpointListener();
			this.initHeightMonitorWithAutoDetect();
		},

		beforeDestroy() {
			this.destroyAllListeners();
		},

		methods: {
			// ——————————————————————
			// 1. 响应式字体 (rem)
			// ——————————————————————
			initResponsiveFont() {
				this._fontSizeListener = () => {
					const width = window.innerWidth;
					const baseSize = Math.max(width / 10800 * 100, 10.65); // min: 10.65px
					document.documentElement.style.fontSize = `${baseSize}px`;
					// 开发时可开启：console.log('🔧 rem:', baseSize);
				};

				this._fontSizeListener(); // 初始化
				window.addEventListener('resize', this._fontSizeListener);
			},

			// ——————————————————————
			// 2. 断点监听（< 1030px 记录）
			// ——————————————————————
			initBreakpointListener() {
				this._resizeListener = () => {
					const isSmall = window.innerWidth <= 1030;
					sessionStorage.setItem('minw', String(isSmall));
				};

				this._resizeListener(); // 初始化
				window.addEventListener('resize', this._resizeListener);
			},

			// ——————————————————————
			// 3. 高度监控（智能检测 DOM 渲染完成）
			// ——————————————————————
			initHeightMonitorWithAutoDetect() {
				// 清理旧资源
				if (this._ro) {
					this._ro.disconnect();
					this._ro = null;
				}
				if (this._interval) {
					clearInterval(this._interval);
					this._interval = null;
				}
				if (this._mo) {
					this._mo.disconnect();
					this._mo = null;
				}

				const updateCellHeight = () => {
					const tenantSearchList = document.querySelectorAll('.tenant_search');
					const myCellList = document.querySelectorAll('.Mycell');

					if (myCellList.length === 0) return;

					let targetHeight;

					if (tenantSearchList.length === 0) {
						targetHeight = '60vh';

					} else {
						let maxHeight = 0;
						Array.prototype.forEach.call(tenantSearchList, el => {
							const h = el.offsetHeight;
							if (h > maxHeight) maxHeight = h;
						});
						targetHeight = maxHeight > 120 ? '20vh' : '50vh';
					}

					Array.prototype.forEach.call(myCellList, el => {
						if (el.style.height !== targetHeight) {
							el.style.height = targetHeight;
						}
					});
				};

				const startObserver = () => {
					if (this._ro) this._ro.disconnect();
					if (this._interval) clearInterval(this._interval);

					const elements = document.querySelectorAll('.tenant_search');

					// ✅ 混合策略：有元素用 ResizeObserver，没有则轮询

					this._interval = setInterval(updateCellHeight, 500); // 轮询保活


					updateCellHeight(); // 立即执行
				};

				// MutationObserver：兼容写法
				const observer = new MutationObserver(function(mutations) {
					for (var i = 0; i < mutations.length; i++) {
						var mutation = mutations[i];
						if (mutation.type === 'childList') {
							var addedNodes = Array.prototype.slice.call(mutation.addedNodes || []);
							var hasTenantSearch = false;

							for (var j = 0; j < addedNodes.length; j++) {
								var node = addedNodes[j];
								if (node.nodeType !== 1) continue;

								if (node.classList && node.classList.contains('tenant_search')) {
									hasTenantSearch = true;
									break;
								}

								if (typeof node.querySelectorAll === 'function') {
									try {
										if (node.querySelectorAll('.tenant_search').length > 0) {
											hasTenantSearch = true;
											break;
										}
									} catch (e) {}
								}
							}

							if (hasTenantSearch) {
								startObserver(); // 重新绑定
								break;
							}
						}
					}
				});

				observer.observe(this.$el, {
					childList: true,
					subtree: true
				});
				this._mo = observer;

				// 初始检测
				this.$nextTick(() => {
					setTimeout(startObserver, 50);
				});

				// 销毁钩子
				this.$on('hook:beforeDestroy', () => {
					if (this._ro) this._ro.disconnect();
					if (this._interval) clearInterval(this._interval);
					if (this._mo) this._mo.disconnect();
					this._ro = this._interval = this._mo = null;
				});
			},
			// ——————————————————————
			// 4. 统一销毁所有监听器
			// ——————————————————————
			destroyAllListeners() {
				// 移除事件监听
				if (this._fontSizeListener) {
					window.removeEventListener('resize', this._fontSizeListener);
				}
				if (this._resizeListener) {
					window.removeEventListener('resize', this._resizeListener);
				}

				// 清理观察器
				if (this._ro) this._ro.disconnect();
				if (this._interval) clearInterval(this._interval);
				if (this._mo) this._mo.disconnect();

				// 清理 storage
				sessionStorage.removeItem('minw');
			}
		}
	};
</script>

<style>
	/* 全局重置 */
	* {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
	}

	body {
		width: 100vw;
		height: 100vh;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
	}

	#app {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
	}

	/* Element Plus 自定义边框色 */
	.el-input__inner,
	.el-radio__inner,
	.el-tabs__nav,
	.el-textarea__inner {
		border-color: var(--el-input__inner, #dcdfe6) !important;
	}

	.el-tabs__item {
		border-left-color: var(--el-input__inner, #dcdfe6) !important;
	}

	/* 小弹窗适配 */
	.my-confirm-dialog {
		width: 330px !important;
	}

	/* 淡入淡出动画 */
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.6s ease;
	}

	.fade-enter-from {
		opacity: 0;
	}

	.fade-enter-to {
		opacity: 1;
	}

	.fade-leave-from {
		opacity: 1;
	}

	.fade-leave-to {
		opacity: 0;
	}
</style>